package dev.wyns.ventrova.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.wyns.ventrova.data.model.MenuDiet
import dev.wyns.ventrova.data.model.MenuItem
import dev.wyns.ventrova.data.model.MenuStats
import dev.wyns.ventrova.data.model.Place
import dev.wyns.ventrova.data.model.VeganLevel
import dev.wyns.ventrova.resources.Res
import dev.wyns.ventrova.resources.dish_tag_vegan
import dev.wyns.ventrova.resources.dish_tag_vegan_likely
import dev.wyns.ventrova.resources.dish_tag_vegan_on_request
import dev.wyns.ventrova.resources.dish_tag_vegetarian
import dev.wyns.ventrova.resources.dish_tag_vegetarian_likely
import dev.wyns.ventrova.resources.ic_info
import dev.wyns.ventrova.resources.menu_inferred_notice
import dev.wyns.ventrova.resources.menu_show_all
import dev.wyns.ventrova.resources.menu_show_less
import dev.wyns.ventrova.resources.menu_source
import dev.wyns.ventrova.resources.menu_summary
import dev.wyns.ventrova.resources.menu_summary_all_vegan
import dev.wyns.ventrova.resources.menu_summary_vegetarian
import dev.wyns.ventrova.resources.menu_title
import dev.wyns.ventrova.resources.menu_title_vegan
import dev.wyns.ventrova.ui.theme.LevelPalettes
import dev.wyns.ventrova.ui.theme.VentrovaColors
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

/** Tag next to a dish, with the colors of the design's dish chips. */
enum class DishTag(val label: StringResource, val background: Color, val color: Color, val inferred: Boolean = false) {
    Vegan(Res.string.dish_tag_vegan, LevelPalettes.Vegan.soft, LevelPalettes.Vegan.ink),
    VeganLikely(Res.string.dish_tag_vegan_likely, LevelPalettes.Vegan.soft, LevelPalettes.Vegan.ink, inferred = true),
    VeganOnRequest(Res.string.dish_tag_vegan_on_request, Color(0xFFF1EFEA), LevelPalettes.VeganOptions.ink),
    Vegetarian(Res.string.dish_tag_vegetarian, LevelPalettes.Vegetarian.soft, LevelPalettes.Vegetarian.ink),
    VegetarianLikely(Res.string.dish_tag_vegetarian_likely, LevelPalettes.Vegetarian.soft, LevelPalettes.Vegetarian.ink, inferred = true),
}

data class MenuDish(val item: MenuItem, val tag: DishTag)

/**
 * The dishes worth showing, vegan first, then vegan on request, then vegetarian, each in menu
 * order. A vegan place shows its whole menu; a vegetarian place shows every dish, since none has
 * meat. Dishes the menu doesn't mark itself are tagged as "probably".
 */
fun menuDishes(place: Place, items: List<MenuItem>): List<MenuDish> = when (place.veganLevel) {
    VeganLevel.Vegan -> items.map { MenuDish(it, DishTag.Vegan) }
    VeganLevel.Vegetarian -> items.map { item ->
        when (item.diet) {
            MenuDiet.Vegan -> MenuDish(item, if (item.labeled) DishTag.Vegan else DishTag.VeganLikely)
            MenuDiet.VeganOnRequest -> MenuDish(item, DishTag.VeganOnRequest)
            else -> MenuDish(item, DishTag.Vegetarian)
        }
    }
    VeganLevel.VeganOptions -> items.mapNotNull { item ->
        when (item.diet) {
            MenuDiet.Vegan -> MenuDish(item, if (item.labeled) DishTag.Vegan else DishTag.VeganLikely)
            MenuDiet.VeganOnRequest -> MenuDish(item, DishTag.VeganOnRequest)
            MenuDiet.Vegetarian -> MenuDish(item, if (item.labeled) DishTag.Vegetarian else DishTag.VegetarianLikely)
            else -> null
        }
    }
}.sortedBy {
    when (it.tag) {
        DishTag.Vegan, DishTag.VeganLikely -> 0
        DishTag.VeganOnRequest -> 1
        DishTag.Vegetarian, DishTag.VegetarianLikely -> 2
    }
}

/** Per-platform look of the dish rows, taken from the three designs. */
data class MenuStyle(
    val nameSize: TextUnit,
    val nameWeight: FontWeight,
    /** Size of the dish description, shown muted under the name when the menu has one. */
    val descriptionSize: TextUnit,
    val rowPadding: Dp,
    val tagSize: TextUnit,
    val tagRadius: Dp,
    val tagPadding: Dp,
    val tagTop: Dp,
    val divider: Color,
    val dividerWidth: Dp,
    /** Android draws the divider above each row, iOS and web below. */
    val dividerAbove: Boolean,
    val link: Color,
    val summarySize: TextUnit,
    /** Background of the "probably" notice, set off from the surface the menu sits on. */
    val noticeBackground: Color,
) {
    companion object {
        val Material = MenuStyle(
            nameSize = 15.sp, nameWeight = FontWeight.Normal, descriptionSize = 13.sp, rowPadding = 10.dp, tagSize = 11.5.sp, tagRadius = 6.dp,
            tagPadding = 8.dp, tagTop = 4.dp, divider = Color(0xFFE1DFD7), dividerWidth = 1.dp,
            dividerAbove = true, link = VentrovaColors.AccentDark, summarySize = 14.sp, noticeBackground = Color.White.copy(alpha = 0.7f),
        )
        val Ios = MenuStyle(
            nameSize = 15.5.sp, nameWeight = FontWeight(550), descriptionSize = 13.5.sp, rowPadding = 12.dp, tagSize = 11.5.sp, tagRadius = 6.dp,
            tagPadding = 8.dp, tagTop = 5.dp, divider = VentrovaColors.Ink.copy(alpha = 0.1f), dividerWidth = 0.5.dp,
            dividerAbove = false, link = VentrovaColors.Accent, summarySize = 14.sp, noticeBackground = Color.White,
        )
        val Web = MenuStyle(
            nameSize = 14.5.sp, nameWeight = FontWeight.Normal, descriptionSize = 12.5.sp, rowPadding = 10.dp, tagSize = 11.sp, tagRadius = 5.dp,
            tagPadding = 7.dp, tagTop = 4.dp, divider = Color(0xFFECE9E1), dividerWidth = 1.dp,
            dividerAbove = false, link = VentrovaColors.Accent, summarySize = 13.5.sp, noticeBackground = VentrovaColors.Sand,
        )
    }
}

/** Title above the menu: the design's "Favorites from the menu" for vegan places. */
@Composable
fun menuTitle(place: Place): String =
    stringResource(if (place.veganLevel == VeganLevel.Vegan) Res.string.menu_title_vegan else Res.string.menu_title)

/** Menu ring with a line like "8 of 24 dishes vegan · 5 vegetarian". */
@Composable
fun MenuSummary(place: Place, stats: MenuStats, style: MenuStyle, modifier: Modifier = Modifier) {
    val text = when (place.veganLevel) {
        VeganLevel.Vegan -> pluralStringResource(Res.plurals.menu_summary_all_vegan, stats.foodItems, stats.foodItems)
        VeganLevel.Vegetarian -> pluralStringResource(Res.plurals.menu_summary_vegetarian, stats.foodItems, stats.veganItems, stats.foodItems)
        VeganLevel.VeganOptions -> pluralStringResource(Res.plurals.menu_summary, stats.foodItems, stats.veganItems, stats.foodItems, stats.vegetarianItems)
    }
    Row(modifier, verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        MenuRing(place.menuShare, width = 3.5.dp, modifier = Modifier.size(20.dp))
        Text(text, style = bodyStyle(style.summarySize, color = VentrovaColors.Secondary))
    }
}

/**
 * Summary, dish rows and a "show all" toggle; the title is laid out by each platform. [items] is
 * null while the dishes load. Shows nothing for a place without an analysed menu.
 */
@Composable
fun MenuDishes(
    place: Place,
    items: List<MenuItem>?,
    style: MenuStyle,
    modifier: Modifier = Modifier,
    linkModifier: Modifier = Modifier,
    collapsedCount: Int = 8,
) {
    val stats = place.menu ?: return
    var expanded by rememberSaveable(place.id) { mutableStateOf(false) }
    Column(modifier) {
        MenuSummary(place, stats, style, Modifier.padding(bottom = 6.dp))
        if (items == null) {
            repeat(3) { DishRowSkeleton(style) }
            return@Column
        }
        val dishes = menuDishes(place, items)
        if (dishes.any { it.tag.inferred }) InferredNotice(style, Modifier.padding(top = 4.dp, bottom = 6.dp))
        val shown = if (expanded) dishes else dishes.take(collapsedCount)
        shown.forEach { DishRow(it, style) }
        if (dishes.size > collapsedCount) {
            Text(
                if (expanded) stringResource(Res.string.menu_show_less) else stringResource(Res.string.menu_show_all, dishes.size),
                style = bodyStyle(style.nameSize, FontWeight.SemiBold, style.link),
                modifier = linkModifier.padding(vertical = 10.dp).clickable { expanded = !expanded },
            )
        }
        Text(
            stringResource(Res.string.menu_source),
            style = bodyStyle(12.5.sp, color = VentrovaColors.Muted),
            modifier = Modifier.padding(top = 8.dp, bottom = 4.dp),
        )
    }
}

/** Warns that "probably" dishes aren't marked as such on the menu and should be checked. */
@Composable
private fun InferredNotice(style: MenuStyle, modifier: Modifier = Modifier) {
    Row(
        modifier.fillMaxWidth().background(style.noticeBackground, RoundedCornerShape(12.dp)).padding(horizontal = 12.dp, vertical = 10.dp),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        VIcon(Res.drawable.ic_info, size = 18.dp, tint = VentrovaColors.Secondary)
        Text(stringResource(Res.string.menu_inferred_notice), style = bodyStyle(13.sp, color = VentrovaColors.Secondary).copy(lineHeight = 18.sp))
    }
}

@Composable
private fun DishRow(dish: MenuDish, style: MenuStyle) {
    Column {
        if (style.dividerAbove) Divider(style)
        Row(Modifier.fillMaxWidth().padding(vertical = style.rowPadding), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            Column(Modifier.weight(1f)) {
                Text(dish.item.name, style = bodyStyle(style.nameSize, style.nameWeight).copy(lineHeight = style.nameSize * 1.3f))
                dish.item.description?.takeUnless { it.equals(dish.item.name, ignoreCase = true) }?.let { description ->
                    Text(
                        description,
                        style = bodyStyle(style.descriptionSize, color = VentrovaColors.Muted).copy(lineHeight = style.descriptionSize * 1.35f),
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.padding(top = 2.dp),
                    )
                }
                Text(
                    stringResource(dish.tag.label),
                    style = bodyStyle(style.tagSize, FontWeight(650), dish.tag.color),
                    modifier = Modifier
                        .padding(top = style.tagTop)
                        .background(dish.tag.background, RoundedCornerShape(style.tagRadius))
                        .padding(horizontal = style.tagPadding, vertical = 2.dp),
                )
            }
        }
        if (!style.dividerAbove) Divider(style)
    }
}

@Composable
private fun DishRowSkeleton(style: MenuStyle) {
    Column {
        if (style.dividerAbove) Divider(style)
        Row(Modifier.fillMaxWidth().padding(vertical = style.rowPadding), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                SkeletonLine(180.dp, height = 13.dp)
                SkeletonLine(56.dp, height = 14.dp)
            }
        }
        if (!style.dividerAbove) Divider(style)
    }
}

@Composable
private fun Divider(style: MenuStyle) {
    Box(Modifier.fillMaxWidth().height(style.dividerWidth).background(style.divider))
}
